import React, { useCallback, useMemo } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { EstimateSplitCardProps } from "../types";
import { calculateSupplierFulfillingItemsMRP } from "../utils/deliveryItemUtils";
import { useDeliverySelectionValidation } from "../validation/useDeliverySelectionValidation";
import { useManualOptionHandlers } from "../hooks/useManualOptionHandlers";
import { MANUAL_DELIVERY_OPTION_RADIO_VALUE } from "../utils/deliveryOptionSelectionUtils";
import {
  getAddManualDeliveryRestrictionMessage,
  getDeleteDeliveryOptionRestrictionMessage,
  getManualDeliveryEditPermission,
  ManualDeliveryEditPermission,
} from "../utils/manualDeliveryOptionEditPermission";
import {
  buildTradingSelfPickupValidationContext,
  isTradingSelfPickupAllowed,
} from "../utils/estimateTradingSelfPickupValidation";
import { checkCustomerHasMgpAssociation, getParentSupplierId } from "../utils/resolveDefaultEstimateSplitTransactionType";
import { useEstimateSplitSelection } from "./hooks/useEstimateSplitSelection";
import { useEstimateSplitMutations } from "./hooks/useEstimateSplitMutations";
import { useBuyTimeInput } from "./hooks/useBuyTimeInput";
import { SupplierSplitHeader } from "./components/SupplierSplitHeader";
import { SplitActionPills } from "./components/SplitActionPills";
import { PartsListColumn } from "./components/PartsListColumn";
import { DeliveryOptionsPanel } from "./components/DeliveryOptionsPanel";
import { getFullPaymentRouteLabel } from "./utils/paymentRouteLabels";
import { ReadOnlySplitActionDisplay } from "./components/ReadOnlySplitActionDisplay";
import { useGetOrderMediaConfig } from "../../../views/PlaceOrderTest/queryHooks";
import { useGetAllSuppliers } from "../../../views/Orders/queryHooks";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import {
  isSourceSupplierInList,
  parseWhitelistedSuppliersForDeliveryOption,
} from "../utils/parseWhitelistedSuppliersForDeliveryOption";
import { isVbzCustomerTngSplitFromOrder } from "../utils/isVbzCustomerTngSplitCase";

const EstimateSplitCard: React.FC<EstimateSplitCardProps> = ({
  estimateNo,
  orderNo,
  supplier,
  selection,
  onSelectionChange,
  isExpired,
  quickEtasMins,
  instantDeliveryMinimumDeliveryCost,
  orderData,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired,
  onOptionAdd,
  onOptionDelete,
  onSplitConfigApplied,
  isReadOnly = false,
  canRemoveParts = false,
  onRemovePart,
}) => {
  const isRemainingItems =
    supplier.supplierId === null || supplier.supplierId === undefined;

  const { data: mobileConfigs, isLoading: isMobileConfigLoading, isError: isMobileConfigError } =
    useGetOrderMediaConfig();
  const { data: allSuppliers } = useGetAllSuppliers();
  const userDetails = useUserDetailsContext();

  const whitelistedSuppliersForRestrictedDeliveryOption = useMemo(
    () =>
      parseWhitelistedSuppliersForDeliveryOption(
        mobileConfigs?.whitelisted_suppliers_for_restricted_delivery_option,
      ),
    [mobileConfigs?.whitelisted_suppliers_for_restricted_delivery_option],
  );

  const isCustomerAssociatedWithGaraaz = useMemo(() => {
    if (!garaazSupplierIdFromConfig) return false;
    return checkCustomerHasMgpAssociation(
      orderData,
      garaazSupplierIdFromConfig,
      allSuppliers,
    );
  }, [orderData, garaazSupplierIdFromConfig, allSuppliers]);

  const sourceSupplierIdForRouting = useMemo(() => {
    const fromOptions = supplier.supplierOptions?.find(
      (opt) => !opt.isAssociated,
    )?.supplierId;
    return fromOptions || supplier.supplierId || undefined;
  }, [supplier.supplierOptions, supplier.supplierId]);


  const isRestrictedSupplier = useMemo(
    () =>
      isSourceSupplierInList(
        sourceSupplierIdForRouting,
        whitelistedSuppliersForRestrictedDeliveryOption.manualFulfillmentRoutingSuppliers,
      ),
    [
      sourceSupplierIdForRouting,
      whitelistedSuppliersForRestrictedDeliveryOption.manualFulfillmentRoutingSuppliers,
    ],
  );

  const isManualFulfillmentRoutingRestricted = useMemo(
    () => {
      if (isCustomerAssociatedWithGaraaz) {
        return false;
      }
      return isRestrictedSupplier;
    },
    [isCustomerAssociatedWithGaraaz, isRestrictedSupplier],
  );

  const isVbzCustomerTngSplitCase = useMemo(
    () =>
      isVbzCustomerTngSplitFromOrder(orderData, supplier.supplierId ?? undefined),
    [orderData, supplier.supplierId],
  );

  const augmentedSupplierOptions = useMemo(() => {
    let options = supplier.supplierOptions || [];
    
    if (isCustomerAssociatedWithGaraaz && isRestrictedSupplier && allSuppliers?.length) {
      const getStrId = (val: any) => {
        if (!val) return "";
        if (typeof val === "object") {
          if (val.$oid) return String(val.$oid).trim().toLowerCase();
          if (val._id) return String(val._id).trim().toLowerCase();
          return "";
        }
        return String(val).trim().toLowerCase();
      };

      const sourceId = getStrId(sourceSupplierIdForRouting);
      const sourceSupplier = allSuppliers.find((s: any) => getStrId(s._id) === sourceId);
      
      console.log("[augmentedSupplierOptions] sourceId:", sourceId, "sourceSupplier:", !!sourceSupplier, "isCustomerAssociatedWithGaraaz:", isCustomerAssociatedWithGaraaz, "isRestrictedSupplier:", isRestrictedSupplier);

      if (sourceId) {
        // Find the parentSupplierId using the robust helper which checks orderData as well
        const resolvedParentId = getStrId(getParentSupplierId(sourceId, orderData)) || getStrId(sourceSupplier?.parentSupplierId) || sourceId;
        console.log("[augmentedSupplierOptions] resolvedParentId:", resolvedParentId);
        
        const siblings = allSuppliers.filter((s: any) => {
          const sParentId = getStrId(s.parentSupplierId);
          const sId = getStrId(s._id);
          // A sibling is either another supplier with the SAME parentSupplierId,
          // OR the parent supplier itself
          return (sParentId && sParentId === resolvedParentId) || sId === resolvedParentId;
        });
        
        console.log("[augmentedSupplierOptions] siblings found:", siblings.map((s: any) => getStrId(s._id)));
        
        const newOptions = [...options];
        siblings.forEach((sib: any) => {
          const sibId = getStrId(sib._id);
          if (!newOptions.find(o => getStrId(o.supplierId) === sibId)) {
            newOptions.push({
              supplierId: sibId,
              businessName: sib.businessName || sib.name || "",
              branchName: sib.branchName || "",
              branchCode: sib.branchCode || "",
              isAssociated: false,
              address: sib.address
            });
          }
        });
        
        console.log("[augmentedSupplierOptions] final options count:", newOptions.length);
        return newOptions;
      }
    }
    return options;
  }, [
    supplier.supplierOptions,
    isCustomerAssociatedWithGaraaz,
    isRestrictedSupplier,
    allSuppliers,
    sourceSupplierIdForRouting,
    orderData
  ]);

  const supplierWithAugmentedOptions = useMemo(
    () => ({ ...supplier, supplierOptions: augmentedSupplierOptions }),
    [supplier, augmentedSupplierOptions]
  );

  const manualDeliveryEditPermission = useMemo<ManualDeliveryEditPermission>(() => {
    // Fail closed until permission config is available (loading or fetch error).
    if (isMobileConfigLoading || isMobileConfigError || mobileConfigs == null) {
      return { canEdit: false, reason: null };
    }

    return getManualDeliveryEditPermission({
      orderData,
      orderNo,
      userId: userDetails?._id,
      whitelistRaw: mobileConfigs.whitelisted_field_for_new_delivery_option,
      allowedOrderNosRaw: mobileConfigs.orca_allow_md_order_nos,
      allowedUserIdsRaw: mobileConfigs.override_edit_user_ids,
      splitSupplierId: supplier.supplierId,
    });
  }, [
    orderData,
    orderNo,
    userDetails?._id,
    mobileConfigs,
    isMobileConfigLoading,
    isMobileConfigError,
    supplier.supplierId,
  ]);

  const canEditManualDelivery = manualDeliveryEditPermission.canEdit;
  const addManualDeliveryDisabledTooltip = getAddManualDeliveryRestrictionMessage(
    manualDeliveryEditPermission.reason,
  );
  const deleteManualDeliveryDisabledTooltip =
    getDeleteDeliveryOptionRestrictionMessage(
      manualDeliveryEditPermission.reason,
    );

  const supplierFulfillingItemsMRP = useMemo(
    () => calculateSupplierFulfillingItemsMRP(supplier),
    [supplier],
  );

  const tradingSelfPickupContext = useMemo(
    () =>
      buildTradingSelfPickupValidationContext({
        supplierId: supplier.supplierId,
        orderData,
        mgpDistributorConfig: mobileConfigs?.mgp_distributor,
        allSuppliers,
      }),
    [supplier.supplierId, orderData, mobileConfigs?.mgp_distributor, allSuppliers],
  );

  const allowTradingSelfPickup = useMemo(
    () =>
      isTradingSelfPickupAllowed(supplier.supplierId, tradingSelfPickupContext),
    [supplier.supplierId, tradingSelfPickupContext],
  );

  const { errorsByField } = useDeliverySelectionValidation(selection, {
    instantDeliveryMinimumDeliveryCost,
    transactionType: supplierWithAugmentedOptions.transactionType,
    allowTradingSelfPickup,
    isManualFulfillmentRoutingRestricted,
    isVbzCustomerTngSplitCase,
    supplierOptions: supplierWithAugmentedOptions.supplierOptions,
  });

  const splitSelection = useEstimateSplitSelection({
    supplier: supplierWithAugmentedOptions,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    isVbzCustomerTngSplitCase,
  });

  const buyTime = useBuyTimeInput({
    initialBuyTimeMinutes: supplier.buyTime,
    supplierId: supplier.supplierId,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    isExpired,
  });

  const mutations = useEstimateSplitMutations({
    estimateNo,
    supplier: supplierWithAugmentedOptions,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    onOptionDelete: onOptionDelete || (async () => {}),
    onOptionAdd: onOptionAdd || (() => {}),
    instantDeliveryMinimumDeliveryCost,
    isManualSelected: splitSelection.isManualSelected,
    isExpired,
    tradingSelfPickupContext,
    isManualFulfillmentRoutingRestricted,
    isVbzCustomerTngSplitCase,
  });

  const {
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
    handleManualSupplierChange,
  } = useManualOptionHandlers({
    isExpired,
    quickEtasMins,
    currentManualEntry: selection?.deliveryDetails?.manualEntry,
    onManualEntryChange: splitSelection.handleManualOptionUpdate,
    supplierOptions: supplierWithAugmentedOptions.supplierOptions,
    isManualFulfillmentRoutingRestricted,
    isVbzCustomerTngSplitCase,
  });

  const displayTitle = isRemainingItems
    ? "Supplier Not Found"
    : supplier.supplierName;

  const paymentRouteLabel = useMemo(
    () =>
      getFullPaymentRouteLabel(supplier.payableBy, supplier.payableTo),
    [supplier.payableBy, supplier.payableTo],
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      if (value === MANUAL_DELIVERY_OPTION_RADIO_VALUE) {
        splitSelection.handleManualOptionSelect();
      } else {
        splitSelection.handleDeliveryOptionSelect(value);
      }
    },
    [splitSelection],
  );

  const handleManualEntryCancel = useCallback(() => {
    splitSelection.handleManualEntryCancel(isExpired);
  }, [splitSelection, isExpired]);

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="orange.300"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
    >
      <SupplierSplitHeader
        displayTitle={displayTitle}
        supplier={supplier}
        isRemainingItems={isRemainingItems}
        supplierFulfillingItemsMRP={supplierFulfillingItemsMRP}
        actions={
          isReadOnly ? (
            <ReadOnlySplitActionDisplay
              status={supplier.status}
              buyTimeMinutes={supplier.buyTime}
            />
          ) : (
            <SplitActionPills
              selection={selection}
              isExpired={isExpired}
              buyTimeValue={buyTime.buyTimeValue}
              isBuyTimeSelected={splitSelection.isBuyTimeSelected}
              onHold={splitSelection.handleHold}
              onCancel={splitSelection.handleCancel}
              onBuyTimeToggle={buyTime.handleBuyTimeToggle}
              onBuyTimeValueChange={buyTime.handleBuyTimeValueChange}
            />
          )
        }
      />

      <Flex direction={{ base: "column", md: "row" }}>
        <PartsListColumn
          parts={supplier.parts}
          estimateNo={estimateNo}
          splitId={supplier.splitId}
          supplierId={supplier.supplierId}
          orderData={orderData}
          orderNo={orderNo}
          garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
          isSingleGaraazInvoiceRequired={isSingleGaraazInvoiceRequired}
          isRemainingItems={isRemainingItems}
          transactionType={supplier.transactionType}
          discountPercentage={supplier.discountPercentage}
          creditNotePercentage={supplier.creditNotePercentage}
          fulfillmentStatus={supplier.fulfillmentStatus}
          paymentRouteFromLabel={paymentRouteLabel.from}
          paymentRouteToLabel={paymentRouteLabel.to}
          payableBy={supplier.payableBy}
          payableTo={supplier.payableTo}
          isExpired={isExpired}
          onSplitConfigApplied={onSplitConfigApplied || (() => {})}
          isReadOnly={isReadOnly}
          canRemoveParts={
            canRemoveParts && !isRemainingItems && !isReadOnly
          }
          onRemovePart={onRemovePart}
        />
        <DeliveryOptionsPanel
          supplier={supplierWithAugmentedOptions}
          selection={selection}
          estimateNo={estimateNo}
          isExpired={isExpired}
          isRemainingItems={isRemainingItems}
          radioValue={splitSelection.radioValue}
          isManualSelected={splitSelection.isManualSelected}
          isDeliveryOptionSelected={splitSelection.isDeliveryOptionSelected}
          errorsByField={errorsByField}
          quickEtaOptions={quickEtaOptions}
          manualSaveError={mutations.manualSaveError}
          isSavingManualOption={mutations.isSavingManualOption}
          isDeleteDisabled={mutations.isDeleteDisabled}
          isDeletingOption={mutations.isDeletingOption}
          onRadioChange={handleRadioChange}
          onDeliveryOptionSelect={splitSelection.handleDeliveryOptionSelect}
          onManualOptionSelect={splitSelection.handleManualOptionSelect}
          onOptionDelete={mutations.handleOptionDelete}
          onManualCPTSlabChange={handleManualCPTSlabChange}
          onManualCPTChange={handleManualCPTChange}
          onManualChargeChange={handleManualChargeChange}
          onQuickEtaSelect={handleQuickEtaSelect}
          onManualEntryCancel={handleManualEntryCancel}
          onManualEntrySave={mutations.handleManualEntrySave}
          onManualSupplierChange={handleManualSupplierChange}
          onOptionSupplierChange={splitSelection.handleOptionSupplierChange}
          isReadOnly={isReadOnly}
          canEditManualDelivery={canEditManualDelivery}
          addManualDeliveryDisabledTooltip={addManualDeliveryDisabledTooltip}
          deleteManualDeliveryDisabledTooltip={
            deleteManualDeliveryDisabledTooltip
          }
          allowTradingSelfPickup={allowTradingSelfPickup}
          isManualFulfillmentRoutingRestricted={
            isManualFulfillmentRoutingRestricted
          }
          isVbzCustomerTngSplitCase={isVbzCustomerTngSplitCase}
        />
      </Flex>
    </Box>
  );
};

export default EstimateSplitCard;
